import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MaintenanceRecord } from '../../../../../types/api';
import {
  formatDate,
  getMaintenanceTypeDisplayName,
  preloadMaintenanceTypeDisplayNames,
} from '../../../../../utils/maintenanceTypeUtils';
import { useTranslation } from '../../../../../i18n';

// Styled component matching existing design
const StyledMaintenanceCard = styled(Box)({
  padding: '12px',
  marginBottom: '8px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  backgroundColor: '#fafafa',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
});

// Now using shared MaintenanceRecord type from api.ts

interface MaintenanceRecordCardProps {
  record: MaintenanceRecord;
  onClick?: (record: MaintenanceRecord) => void;
  showWorkOrderId?: boolean;
}

export const MaintenanceRecordCard: React.FC<MaintenanceRecordCardProps> = ({
  record,
  onClick,
  showWorkOrderId = true,
}) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState<string>('');

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(record);
  };

  // Handle potential legacy date field
  const recordWithLegacy = record as MaintenanceRecord & { date?: string; type?: string };
  const startDate = record.start_date || recordWithLegacy.date || '';
  const endDate = record.end_date || recordWithLegacy.date || '';
  const maintenanceType = record.maintenance_type || recordWithLegacy.type || '';
  const workDescription = record.work_description;

  // Load display name for maintenance type
  useEffect(() => {
    const loadDisplayName = async () => {
      // Preload cache first time
      await preloadMaintenanceTypeDisplayNames();
      const name = await getMaintenanceTypeDisplayName(maintenanceType);
      setDisplayName(name);
    };

    if (maintenanceType) {
      loadDisplayName();
    }
  }, [maintenanceType]);

  return (
    <StyledMaintenanceCard
      onClick={handleClick}
      data-testid="maintenance-record-card"
      data-record-id={record.id}
    >
      {/* Top row: Title and Date */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
          {displayName || maintenanceType}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {startDate && endDate && startDate === endDate
            ? formatDate(startDate)
            : startDate && endDate
              ? `${formatDate(startDate)} - ${formatDate(endDate)}`
              : 'N/A'}
        </Typography>
      </Box>

      {/* Work Order ID (optional) */}
      {showWorkOrderId && record.odoo_work_order_id && (
        <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>
          <strong>{t('maintenanceTab.workOrderId')}</strong> {record.odoo_work_order_id}
        </Typography>
      )}

      {/* Work Description (only show if it exists) */}
      {workDescription && (
        <Typography variant="body2" sx={{ color: '#555', mb: 1 }}>
          {workDescription}
        </Typography>
      )}

      {/* Bottom row: Creator info, Quality checker, and Status */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            {t('maintenanceTab.addedBy')}{' '}
            {record.performed_by_name || record.performed_by || t('maintenanceTab.unknown')}
          </Typography>
          {record.quality_checked && record.quality_checked_by_name && (
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
              {t('maintenanceTab.reviewedBy')} {record.quality_checked_by_name}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {record.quality_checked && (
            <Typography
              variant="caption"
              sx={{
                color: '#2196f3',
                fontWeight: 500,
                backgroundColor: '#e3f2fd',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '0.7rem',
              }}
            >
              {t('maintenanceTab.qualityChecked')}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              color: '#4caf50',
              fontWeight: 500,
              backgroundColor: '#e8f5e8',
              padding: '2px 8px',
              borderRadius: '12px',
            }}
          >
            {record.status || t('maintenanceTab.statusCompleted')}
          </Typography>
        </Box>
      </Box>
    </StyledMaintenanceCard>
  );
};
